import { useState, type ReactNode } from 'react';
import { ROLE_META, type GameId, type RoleId } from '@ringmaster/shared';
import { Glyph, type GlyphName, type GlyphTone } from './Glyph.js';
import { ROLE_PLAY_ORDER, roleCardDefFor } from './roleCardDefs.js';
import { RolePortrait } from './RolePortrait.js';
import { roleAccent } from '../hand/roleGlyph.js';

/**
 * HowToPlay — the shared per-game rules card, so the DEAL onboarding and the
 * lobby "How to play" link show the SAME bullets for a given game.
 * Presentational only.
 *
 * - `HowToPlayCard` renders the bordered card (title + bullets) for a game.
 *   `gameId` defaults to `'bloodmoon'` — every existing no-prop call site
 *   (e.g. discord/desktop/screens/DealContent.tsx's `<HowToPlayCard />`)
 *   keeps rendering the original Blood Moon content unchanged.
 * - `HowToPlayModal` rides it on a full-screen dim backdrop with a Close button,
 *   used from the lobby so players can read the rules while they wait.
 */

interface HowToPlayContent {
  title: string;
  tone: GlyphTone;
  bullets: { glyph: GlyphName; lead: string; rest: ReactNode }[];
  /**
   * Optional interactive role list, spliced in AFTER the bullet at this index
   * (0-based) — replaces what would otherwise be a prose bullet about the
   * roles. Only One Night uses this today (see {@link RoleExplorer}).
   */
  roleExplorer?: {
    afterBulletIndex: number;
    roles: RoleId[];
    heading?: string;
    /**
     * Optional second line under a role's description — for a rule about that
     * role that belongs in the RULES, not on its card. A role card says what you
     * ARE (identity, read once at the deal); this says how your power interacts
     * with the rest of the night, which only matters while learning the game.
     * Keeping it here is why the card copy in roleCardDefs.ts stays one line.
     */
    notes?: Partial<Record<RoleId, string>>;
  };
}

const CONTENT: Record<string, HowToPlayContent> = {
  bloodmoon: {
    title: 'How to Play',
    tone: 'gold',
    bullets: [
      {
        glyph: 'wolfHead',
        lead: 'One player is the Werewolf.',
        rest: ' They blend in while secretly eliminating villagers each night.',
      },
      {
        glyph: 'moonCrescent',
        lead: 'Night = phone acts.',
        rest: ' When your role wakes, your phone lights up. Everyone else stays asleep.',
      },
      {
        glyph: 'ballot',
        lead: 'Day = discuss & vote.',
        rest: ' Talk it out at the table, then tap to vote who to lynch.',
      },
    ],
  },
  whisper: {
    title: 'How to Play',
    tone: 'arcana',
    bullets: [
      {
        glyph: 'seerEye',
        lead: 'Ask the Seer anything —',
        rest: ' she holds a secret word and answers honestly, but never plainly.',
      },
      {
        glyph: 'candle',
        lead: 'The candle is your clock.',
        rest: ' Guess freely, but she reads one whisper at a time — say the word before the light dies.',
      },
      {
        glyph: 'scales',
        lead: 'One of you may be the Deceiver,',
        rest: ' who already knows the word and wants the candle to win.',
      },
      {
        glyph: 'closedEye',
        lead: 'When the flame settles,',
        rest: ' point your finger — unmask the Deceiver.',
      },
    ],
  },
  onenight: {
    title: 'How to Play',
    tone: 'gold',
    bullets: [
      {
        glyph: 'wolfHead',
        lead: 'All active roles act at once.',
        rest: ' One night, no elimination — then everyone wakes for a single vote.',
      },
      {
        glyph: 'candle',
        lead: 'Three cards never reach a seat.',
        rest: ' They sit face-down in the centre — both wolves could be sitting right there, watching the villagers argue.',
      },
      {
        glyph: 'ballot',
        lead: 'One argument, one vote.',
        rest: ' No elimination round, no second night — say your piece and vote once, together.',
      },
    ],
    roleExplorer: {
      // afterBulletIndex is 0-based and splices AFTER that bullet — the last
      // bullet's index (bullets.length - 1) puts the role row LAST, after all
      // the rule prose (playtest ask: the roles are a reference, not a rule).
      afterBulletIndex: 2,
      // Order of play, from the one shared list (ui/roleCardDefs.ts) — this row
      // and the lobby role gallery must show the same five cards in the same
      // order, and they used to be two hand-typed arrays.
      roles: ROLE_PLAY_ORDER.onenight,
      heading: '5 roles, each unique',
      notes: {
        // Everyone acts in the same beat, so "goes last" is about how the cards
        // SETTLE, not about waiting your turn — and it is the one ordering fact
        // that changes how you read the table (see NIGHT_SWAP_ORDER in the
        // engine: THIEF 1, JOKER 2).
        JOKER:
          'The Joker settles last — after the Thief — so it can move a card that was already stolen. One blind swap can send two players’ certainty, and the whole table’s reasoning, somewhere else entirely.',
      },
    },
  },
};

function contentFor(gameId: string): HowToPlayContent {
  return CONTENT[gameId] ?? CONTENT.bloodmoon!;
}

export function HowToPlayCard({ gameId = 'bloodmoon' }: { gameId?: string } = {}) {
  const { title, tone, bullets, roleExplorer } = contentFor(gameId);
  // One Night's five-avatar role row needs a wider card to fit on one line
  // (see RoleExplorer) — widened only for this game's entry so Blood Moon /
  // The Whisper keep their original, narrower card unchanged.
  const maxWidth = gameId === 'onenight' ? 440 : 360;
  return (
    <div
      style={{
        width: '100%',
        maxWidth,
        background: 'rgba(0,0,0,0.7)',
        border: '1px solid var(--surface-stroke)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-6)',
      }}
    >
      <h2
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 22,
          margin: '0 0 var(--space-5)',
          textAlign: 'center',
          color: 'var(--text-primary)',
        }}
      >
        {title}
      </h2>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {bullets.map((b, i) => (
          <li key={b.lead} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
              <span style={{ flexShrink: 0, marginTop: 1, display: 'inline-flex' }}>
                <Glyph name={b.glyph} size={20} tone={tone} />
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{b.lead}</strong>
                {b.rest}
              </span>
            </div>
            {roleExplorer?.afterBulletIndex === i && (
              <RoleExplorer
                gameId={gameId}
                roles={roleExplorer.roles}
                heading={roleExplorer.heading}
                notes={roleExplorer.notes}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * RoleExplorer — a row of tappable role PORTRAIT avatars (the shared
 * {@link RolePortrait} in a faction-accented ring — the same portrait
 * {@link RoleTally} and One Night's roles popover use, never a Glyph icon)
 * standing in for a wall of role prose. Tapping one expands that role's per-GAME description below the row
 * (via roleCardDefFor — NOT the shared ROLE_META.onboarding, which is Blood
 * Moon-specific and wrong for One Night);
 * tapping it again (or a different role) collapses/switches. Starts with
 * nothing expanded. `gameId` resolves the right character art via
 * {@link roleCardDefFor} (e.g. One Night's Thief/Joker borrow art — expected,
 * see roleCardDefs.ts).
 */
function RoleExplorer({
  gameId,
  roles,
  heading,
  notes,
}: {
  gameId: string;
  roles: RoleId[];
  heading?: string;
  notes?: Partial<Record<RoleId, string>>;
}) {
  const [openRole, setOpenRole] = useState<RoleId | null>(null);
  const open = openRole ? ROLE_META[openRole] : null;
  const note = openRole ? notes?.[openRole] : undefined;

  return (
    <div style={{ width: '100%' }}>
      {heading && (
        // Matches the BULLET LEAD treatment in this same modal (14px, sentence
        // case, --text-primary, bold) — NOT the uppercase/letterspaced/tertiary
        // micro-label used for section headers on the deal screen. Same font
        // family is not the same treatment: borrowing another screen's idiom is
        // what read as "a completely different font" in playtest. See the
        // consistency rule in CLAUDE.md.
        <div
          style={{
            fontFamily: 'var(--font-ui)', fontSize: 14, fontWeight: 700,
            color: 'var(--text-primary)', textAlign: 'center', marginBottom: 'var(--space-3)',
          }}
        >
          {heading}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-3)', flexWrap: 'nowrap' }}>
        {roles.map((role) => {
          const accent = roleAccent(role);
          const isOpen = openRole === role;
          return (
            <button
              key={role}
              type="button"
              onClick={() => setOpenRole((prev) => (prev === role ? null : role))}
              aria-pressed={isOpen}
              className="rm-hover"
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                width: 60, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              }}
            >
              <RolePortrait
                gameId={gameId as GameId}
                role={role}
                size={44}
                ring={isOpen ? `2px solid ${accent}` : `1px solid ${accent}`}
                glow={isOpen ? `0 0 16px ${accent}66` : 'none'}
                style={{ transition: 'all var(--duration-fast) var(--ease-standard)' }}
              />
              <span style={{ fontFamily: 'var(--font-ui)', fontSize: 11, color: accent, textAlign: 'center' }}>
                {ROLE_META[role].label}
              </span>
            </button>
          );
        })}
      </div>
      {open && (
        <p
          style={{
            marginTop: 'var(--space-3)', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.5,
            background: 'rgba(255,255,255,0.03)', border: '1px solid var(--surface-stroke)',
            borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)',
          }}
        >
          <strong style={{ color: 'var(--text-primary)' }}>{open.label}.</strong>{' '}
          {/* Game-aware: ROLE_META.onboarding is Blood Moon's multi-night copy
              ("each night", "wolves reach parity"), which is false in One Night.
              roleCardDefFor() already resolves per-game text, so use it and fall
              back to the shared onboarding only where a game has no override. */}
          {roleCardDefFor(gameId as GameId, openRole!).description ?? open.onboarding}
          {note && (
            // Same panel, second paragraph — a rules note, not a second card
            // description, so it reads dimmer than the identity line above it.
            <span style={{ display: 'block', marginTop: 'var(--space-2)', color: 'var(--text-tertiary)' }}>
              {note}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

export function HowToPlayModal({ gameId = 'bloodmoon', onClose }: { gameId?: string; onClose: () => void }) {
  const label =
    gameId === 'whisper'
      ? 'How to play The Whisper'
      : gameId === 'onenight'
        ? 'How to play Blood Moon: One Night'
        : 'How to play Blood Moon';
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'var(--space-5)',
        padding: 'var(--space-5)',
        background: 'rgba(0,0,0,0.82)',
        animation: 'howto-fade 0.2s var(--ease-standard)',
      }}
    >
      {/* Stop propagation so taps on the card don't dismiss the modal. */}
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <HowToPlayCard gameId={gameId} />
      </div>
      <button
        type="button"
        className="rm-hover-pill"
        onClick={onClose}
        style={{
          fontFamily: 'var(--font-ui)',
          fontWeight: 600,
          fontSize: 16,
          borderRadius: 'var(--radius-pill)',
          padding: '14px 40px',
          background: 'var(--btn-primary-bg)',
          color: 'var(--btn-primary-text)',
          border: 'none',
          cursor: 'pointer',
          minHeight: 52,
        }}
      >
        Got it
      </button>
      <style>{`
        @keyframes howto-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
